import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LoadingButton } from '@mui/lab';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { client, useAuth, useI18n } from 'iomart-hooks';
import { ROLES } from 'src/constants/roles';
import Iconify from 'src/components/iconify/Iconify';

export const WB_POSTINGS_RELOAD_EVENT = 'iomart:wb-postings-reload';
type AccountOption = { id: number; name: string; hasApiKey: boolean };
const defaultRange = () => ({ from: dayjs().startOf('month'), to: dayjs().endOf('month') });
const formatDateTime = (value?: string | null) => (!value ? '—' : dayjs(value).format('DD.MM.YYYY HH:mm:ss'));

export function WbPostingsLastSyncMeta() {
  const { role, permissions = [] } = (useAuth(false) as any) ?? {};
  const isAdmin = String(role ?? '').toUpperCase() === ROLES.ADMIN;
  const canSeeAccounts = isAdmin || permissions.includes('wbSellerAccounts');

  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  const loadLastSync = useCallback(async () => {
    if (!canSeeAccounts) return;
    try {
      const res = await (client as any).query({
        listWbSellerAccounts: {
          items: { id: true, lastSyncAt: true },
          __args: {
            category: 'all',
            paging: { page: 0, size: 500 },
            sorting: [{ by: 'desc', order: 'updatedAt' }],
            filters: {},
          },
        },
      });
      const rows = (res?.listWbSellerAccounts?.items ?? []) as Array<{ lastSyncAt?: string | null }>;
      const latest = rows
        .map((x) => (x?.lastSyncAt ? String(x.lastSyncAt) : null))
        .filter(Boolean)
        .sort((a, b) => (String(a) > String(b) ? -1 : 1))[0] as string | undefined;
      setLastSyncAt(latest ?? null);
    } catch {
      setLastSyncAt(null);
    }
  }, [canSeeAccounts]);

  useEffect(() => {
    loadLastSync();
  }, [loadLastSync]);

  if (!canSeeAccounts) {
    return null;
  }

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Iconify icon="mdi:sync" width={16} color="text.secondary" />
      <Typography variant="caption" color="text.secondary">
        {formatDateTime(lastSyncAt)}
      </Typography>
    </Stack>
  );
}

export function WbPostingsSyncToolbar() {
  const { role, userInfo } = useAuth(false) as any;
  const { t } = useI18n(false);
  const { enqueueSnackbar } = useSnackbar();
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  enqueueSnackbarRef.current = enqueueSnackbar;
  const [open, setOpen] = useState(false);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [account, setAccount] = useState<AccountOption | null>(null);
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(() => defaultRange().from);
  const [dateTo, setDateTo] = useState<Dayjs | null>(() => defaultRange().to);
  const [syncing, setSyncing] = useState(false);
  const [syncGetData, setSyncGetData] = useState(true);
  const [includeFbw, setIncludeFbw] = useState(true);
  const [includeFbs, setIncludeFbs] = useState(true);
  const [refreshCostPrices, setRefreshCostPrices] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRuns, setHistoryRuns] = useState<any[]>([]);
  const [historyItems, setHistoryItems] = useState<any[]>([]);
  const [historyAccount, setHistoryAccount] = useState<AccountOption | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [runActionLoadingId, setRunActionLoadingId] = useState<number | null>(null);
  const [intervalMinutes, setIntervalMinutes] = useState<number>(5);
  const [lookbackDays, setLookbackDays] = useState<number>(1);
  const [autoTasks, setAutoTasks] = useState<any[]>([]);
  const [creatingAutoTask, setCreatingAutoTask] = useState(false);
  const [taskActionLoadingId, setTaskActionLoadingId] = useState<number | null>(null);
  const isAdmin = String(role ?? userInfo?.role ?? '').toUpperCase() === 'ADMIN';

  const loadAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    try {
      const res = await (client as any).query({
        listWbSellerAccounts: {
          items: { id: true, name: true, hasApiKey: true },
          __args: { category: 'all', paging: { page: 0, size: 500 }, sorting: [{ by: 'asc', order: 'name' }], filters: {} },
        },
      });
      setAccounts((res?.listWbSellerAccounts?.items ?? []) as AccountOption[]);
    } finally {
      setLoadingAccounts(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    loadAccounts();
    const r = defaultRange();
    setDateFrom(r.from);
    setDateTo(r.to);
  }, [open, loadAccounts]);

  useEffect(() => {
    if (open && !account && accounts.length > 0) setAccount(accounts[0]);
  }, [open, account, accounts]);

  const handleSync = async () => {
    if (!account?.id || !dateFrom || !dateTo) return;
    setSyncing(true);
    try {
      const result = await (client as any).mutation({
        syncWbPostings: {
          count: true,
          __args: {
            data: {
              accountId: account.id,
              dateFrom: dateFrom.startOf('day').toDate(),
              dateTo: dateTo.endOf('day').toDate(),
              syncGetData,
              includeFbw,
              includeFbs,
              refreshCostPrices,
            },
          },
        },
      });
      enqueueSnackbarRef.current(t('Synced {{count}} WB orders', { count: result?.syncWbPostings?.count ?? 0 }), { variant: 'success' });
      setOpen(false);
      window.dispatchEvent(new Event(WB_POSTINGS_RELOAD_EVENT));
    } catch (e: any) {
      enqueueSnackbarRef.current(e?.message ?? t('Sync failed'), { variant: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const loadHistoryRuns = useCallback(async (accountId: number) => {
    setHistoryLoading(true);
    try {
      const res = await (client as any).query({
        wbPostingGetSyncRuns: {
          __args: { data: { accountId, page: 0, size: 100 } },
          total: true,
          items: { id: true, status: true, periodFrom: true, periodTo: true, startedAt: true, finishedAt: true, totalPlanned: true, totalSuccess: true, totalFailed: true, errorSummary: true, createdAt: true },
        },
      });
      setHistoryRuns(res?.wbPostingGetSyncRuns?.items ?? []);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const loadAutoTasks = useCallback(async (accountId?: number) => {
    const res = await (client as any).query({
      wbPostingAutoSyncTasks: {
        __args: { data: { accountId } },
        id: true,
        name: true,
        status: true,
        intervalMinutes: true,
        lookbackDays: true,
        syncGetData: true,
        includeFbw: true,
        includeFbs: true,
        refreshCostPrices: true,
        lastRunAt: true,
        nextRunAt: true,
        lastError: true,
      },
    });
    setAutoTasks(res?.wbPostingAutoSyncTasks ?? []);
  }, []);

  const loadHistoryItems = useCallback(async (runId: number) => {
    const res = await (client as any).query({
      wbPostingGetSyncRunItems: {
        __args: { runId },
        id: true,
        orderId: true,
        postingNumber: true,
        scheme: true,
        status: true,
        attempt: true,
        error: true,
        startedAt: true,
        finishedAt: true,
      },
    });
    setHistoryItems(res?.wbPostingGetSyncRunItems ?? []);
  }, []);

  const handleCreateAutoTask = async () => {
    if (!historyAccount?.id) return;
    setCreatingAutoTask(true);
    try {
      await (client as any).mutation({
        createWbPostingAutoSyncTask: {
          id: true,
          __args: {
            data: {
              accountId: historyAccount.id,
              intervalMinutes: Math.max(1, Number(intervalMinutes || 1)),
              lookbackDays: Math.max(1, Number(lookbackDays || 1)),
              syncGetData,
              includeFbw,
              includeFbs,
              refreshCostPrices,
            },
          },
        },
      });
      enqueueSnackbarRef.current(t('Auto sync task created'), { variant: 'success' });
      loadAutoTasks(historyAccount.id);
    } catch (e: any) {
      enqueueSnackbarRef.current(e?.message ?? t('Failed to create auto sync task'), { variant: 'error' });
    } finally {
      setCreatingAutoTask(false);
    }
  };

  const handleStopAutoTask = async (id: number) => {
    setTaskActionLoadingId(id);
    try {
      await (client as any).mutation({ stopWbPostingAutoSyncTask: { __args: { id } } });
      enqueueSnackbarRef.current(t('Task stopped'), { variant: 'success' });
      if (historyAccount?.id) loadAutoTasks(historyAccount.id);
    } catch (e: any) {
      enqueueSnackbarRef.current(e?.message ?? t('Failed to stop task'), { variant: 'error' });
    } finally {
      setTaskActionLoadingId(null);
    }
  };

  const handleDeleteAutoTask = async (id: number) => {
    setTaskActionLoadingId(id);
    try {
      await (client as any).mutation({ deleteWbPostingAutoSyncTask: { __args: { id } } });
      enqueueSnackbarRef.current(t('Task deleted'), { variant: 'success' });
      if (historyAccount?.id) loadAutoTasks(historyAccount.id);
    } catch (e: any) {
      enqueueSnackbarRef.current(e?.message ?? t('Failed to delete task'), { variant: 'error' });
    } finally {
      setTaskActionLoadingId(null);
    }
  };

  const handleCancelRun = async (runId: number) => {
    setRunActionLoadingId(runId);
    try {
      const res = await (client as any).mutation({
        cancelWbPostingGetSyncRun: { __args: { runId } },
      });
      if (res?.cancelWbPostingGetSyncRun) {
        enqueueSnackbarRef.current(t('Run cancelled'), { variant: 'success' });
      } else {
        enqueueSnackbarRef.current(t('Run cannot be cancelled'), { variant: 'warning' });
      }
      if (historyAccount?.id) {
        await loadHistoryRuns(historyAccount.id);
      }
      if (selectedRunId === runId) {
        await loadHistoryItems(runId);
      }
    } catch (e: any) {
      enqueueSnackbarRef.current(e?.message ?? t('Failed to cancel run'), { variant: 'error' });
    } finally {
      setRunActionLoadingId(null);
    }
  };

  return (
    <>
      {!isAdmin ? null : (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" onClick={() => setOpen(true)}>{t('Sync now')}</Button>
          <IconButton size="small" onClick={() => { setHistoryOpen(true); loadAccounts(); }}>
            <Iconify icon="mdi:history" width={18} />
          </IconButton>
        </Stack>
      )}
      <Drawer anchor="right" open={open} onClose={() => !syncing && setOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 520 }, p: 2 }}>
          <Stack spacing={2.5}>
            <Typography variant="h6">{t('Sync WB orders')}</Typography>
            <Autocomplete
              loading={loadingAccounts}
              options={accounts}
              value={account}
              onChange={(_, v) => setAccount(v)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label={t('WB account')} size="small" required />}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DatePicker label={t('From')} value={dateFrom} onChange={(v) => setDateFrom(v)} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
              <DatePicker label={t('To')} value={dateTo} onChange={(v) => setDateTo(v)} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
            </Stack>
            <FormControlLabel control={<Checkbox checked={syncGetData} onChange={(e) => setSyncGetData(e.target.checked)} />} label={t('Sync details and finance')} />
            <FormControlLabel control={<Checkbox checked={includeFbs} onChange={(e) => setIncludeFbs(e.target.checked)} />} label={t('Include FBS')} />
            <FormControlLabel control={<Checkbox checked={includeFbw} onChange={(e) => setIncludeFbw(e.target.checked)} />} label={t('Include FBW')} />
            <FormControlLabel control={<Checkbox checked={refreshCostPrices} onChange={(e) => setRefreshCostPrices(e.target.checked)} />} label={t('Refresh cost snapshots')} />
            <LoadingButton loading={syncing} variant="contained" onClick={handleSync}>{t('Run sync')}</LoadingButton>
          </Stack>
        </Box>
      </Drawer>
      <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 980 }, p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">{t('Sync history')}</Typography>
            <Autocomplete
              options={accounts}
              value={historyAccount}
              onChange={(_, v) => {
                setHistoryAccount(v);
                setSelectedRunId(null);
                setHistoryItems([]);
                if (v?.id) {
                  loadHistoryRuns(v.id);
                  loadAutoTasks(v.id);
                } else {
                  setAutoTasks([]);
                }
              }}
              getOptionLabel={(o) => o.name}
              renderInput={(params) => <TextField {...params} label={t('WB account')} size="small" />}
            />
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">{t('Auto sync')}</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField
                    size="small"
                    type="number"
                    label={t('Periodicity (minutes)')}
                    value={intervalMinutes}
                    onChange={(e) => setIntervalMinutes(Math.max(1, Number(e.target.value || 1)))}
                    sx={{ minWidth: 180 }}
                  />
                  <TextField
                    size="small"
                    type="number"
                    label={t('Load last days')}
                    value={lookbackDays}
                    onChange={(e) => setLookbackDays(Math.max(1, Number(e.target.value || 1)))}
                    sx={{ minWidth: 160 }}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <FormControlLabel control={<Checkbox checked={syncGetData} onChange={(_, v) => setSyncGetData(v)} />} label={t('Queue get-data sync')} />
                  <FormControlLabel control={<Checkbox checked={refreshCostPrices} onChange={(_, v) => setRefreshCostPrices(v)} />} label={t('Refresh costs')} />
                </Stack>
                <Stack direction="row" justifyContent="flex-end">
                  <LoadingButton size="small" variant="contained" loading={creatingAutoTask} disabled={!historyAccount?.id} onClick={handleCreateAutoTask}>
                    {t('Create auto task')}
                  </LoadingButton>
                </Stack>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Interval</TableCell>
                        <TableCell align="right">Lookback</TableCell>
                        <TableCell>Last run</TableCell>
                        <TableCell>Next run</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {autoTasks.map((task) => (
                        <TableRow key={task.id}>
                          <TableCell>{task.id}</TableCell>
                          <TableCell>{task.status}</TableCell>
                          <TableCell align="right">{task.intervalMinutes}m</TableCell>
                          <TableCell align="right">{task.lookbackDays}d</TableCell>
                          <TableCell>{formatDateTime(task.lastRunAt)}</TableCell>
                          <TableCell>{formatDateTime(task.nextRunAt)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <LoadingButton size="small" color="warning" loading={taskActionLoadingId === task.id} disabled={task.status !== 'active'} onClick={() => handleStopAutoTask(task.id)}>
                                {t('Stop')}
                              </LoadingButton>
                              <LoadingButton size="small" color="error" loading={taskActionLoadingId === task.id} onClick={() => handleDeleteAutoTask(task.id)}>
                                {t('Delete')}
                              </LoadingButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                      {autoTasks.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7}>
                            <Typography variant="body2" color="text.secondary">{t('No auto sync tasks')}</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Paper>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Finished</TableCell>
                    <TableCell align="right">Planned</TableCell>
                    <TableCell align="right">Success</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyRuns.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      selected={selectedRunId === r.id}
                      onClick={() => {
                        setSelectedRunId(r.id);
                        loadHistoryItems(r.id);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.status}</TableCell>
                      <TableCell>{formatDateTime(r.periodFrom)} - {formatDateTime(r.periodTo)}</TableCell>
                      <TableCell>{formatDateTime(r.startedAt)}</TableCell>
                      <TableCell>{formatDateTime(r.finishedAt)}</TableCell>
                      <TableCell align="right">{r.totalPlanned ?? 0}</TableCell>
                      <TableCell align="right">{r.totalSuccess ?? 0}</TableCell>
                      <TableCell align="right">{r.totalFailed ?? 0}</TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <LoadingButton
                          size="small"
                          color="warning"
                          loading={runActionLoadingId === r.id}
                          disabled={!['queued', 'running'].includes(String(r.status))}
                          onClick={() => handleCancelRun(r.id)}
                        >
                          {t('Cancel')}
                        </LoadingButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!historyLoading && historyRuns.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Typography variant="body2" color="text.secondary">{t('No sync runs')}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {selectedRunId != null && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order</TableCell>
                      <TableCell>Scheme</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Attempt</TableCell>
                      <TableCell>Error</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyItems.map((x) => (
                      <TableRow key={x.id}>
                        <TableCell>{x.postingNumber ?? x.orderId ?? '—'}</TableCell>
                        <TableCell>{x.scheme}</TableCell>
                        <TableCell>{x.status}</TableCell>
                        <TableCell align="right">{x.attempt ?? 0}</TableCell>
                        <TableCell>{x.error ?? ''}</TableCell>
                      </TableRow>
                    ))}
                    {historyItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">{t('No run details')}</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Stack direction="row" justifyContent="flex-end">
              <Button onClick={() => setHistoryOpen(false)}>{t('Close')}</Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}

